import Link from "next/link";
import { useRouter } from "next/router";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { twMerge } from "tailwind-merge";

import { faAngleDoubleLeft } from "@fortawesome/free-solid-svg-icons/faAngleDoubleLeft";
import { faAngleDoubleRight } from "@fortawesome/free-solid-svg-icons/faAngleDoubleRight";
import { faSlidersH } from "@fortawesome/free-solid-svg-icons/faSlidersH";

import EntityComparablesButton from "components/DashboardPage/DashboardPageTabs/EntityComparablesButton";
import EntityPrimerButton from "components/DashboardPage/DashboardPageTabs/EntityPrimerButton";
import EntityTopInvestorQuestionButton from "components/DashboardPage/DashboardPageTabs/EntityTopInvestorQuestionButton";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import useSignInSignUp from "components/SignInSignUpModal/useSignInSignUp";
import useActivityTracker from "hooks/useActivityTracker";
import useAuthentication from "hooks/useAuthentication";
import { useGlobalState } from "hooks/useGlobalState";
import useMediaQuery from "hooks/useMediaQuery";
import useWindowSize from "hooks/useWindowSize";

import SmartScriptButton from "./SmartscriptButton";
import styles from "./style.module.scss";

const SHOW_WATCHLISTS_UPDATES = false;

type DashboardPageTabsProps = {};

const DashboardPageTabs = ({}: DashboardPageTabsProps) => {
  const router = useRouter();
  const collection = router.query.collection as string;
  const {
    authenticated,
    account: loggedInAccount,
    interfaceSetting,
    updateInterfaceSetting,
  } = useAuthentication();
  const { toggleModal } = useSignInSignUp();
  const { trackEvent } = useActivityTracker();

  const { mobile } = useMediaQuery();

  const [_, setShowFeedSettings] = useGlobalState(
    "showFeedSettingsModal",
    false
  );

  const tabs = useMemo(
    () => [
      {
        name: "My Feed",
        href: "/dashboard?collection=my-reading-list",
        additional: !mobile && collection === "my-reading-list" && (
          <OverlayTrigger
            trigger={["hover", "focus"]}
            overlay={<Tooltip>Modify Feed Settings</Tooltip>}
          >
            <div className="absolute left-[120px] mt-[9px] ">
              <FontAwesomeIcon
                icon={faSlidersH}
                className="text-[17px] text-smart-teal hover:text-dark-teal cursor-pointer"
                onClick={() =>
                  authenticated
                    ? setShowFeedSettings(true)
                    : toggleModal("sign-up")
                }
              />
            </div>
          </OverlayTrigger>
        ),
      },
      {
        name: "Following",
        href: "/dashboard?collection=from-all-followed",
        otherHrefs: [
          "/dashboard?collection=from-ip-i-follow",
          "/dashboard?collection=followed-collections",
          "/dashboard?collection=from-all-followed",
        ],
      },
      ...(SHOW_WATCHLISTS_UPDATES && !mobile
        ? [
            {
              name: "Watchlist Updates",
              href: "/dashboard?collection=my-watchlists",
              otherHrefs: ["/dashboard?collection=my-watchlists"],
            },
          ]
        : []),
      ...(!mobile
        ? [
            {
              name: "Trending",
              href: "/dashboard?collection=trending",
            },
          ]
        : []),
      { name: "Browse", href: "/dashboard?collection=all-insights" },
      ...(!mobile ? [{ key: "separator-1", type: "separator" }] : []),
      ...(!mobile
        ? [
            {
              name: "My Activity",
              href: "/dashboard?collection=recently-read",
              otherHrefs: [
                "/dashboard?collection=recently-read",
                "/dashboard?collection=bookmarked",
                "/dashboard?collection=liked",
              ],
            },
          ]
        : []),
      ...(loggedInAccount?.isInsightProvider && !mobile
        ? [
            {
              name: (
                <span>
                  Workspace{" "}
                  {!interfaceSetting?.visitedDashboardWorkspace && (
                    <span
                      className={twMerge(
                        "absolute top-[10px] left-[-5px]",
                        "inline-block w-[17px] h-[17px] bg-white border-[5px] border-solid rounded-full",
                        "border-ink-pink"
                      )}
                    />
                  )}
                </span>
              ),
              href: "/dashboard?collection=workspace",
            },
          ]
        : []),
      ...(!mobile
        ? [
            {
              key: "separator-2",
              type: "separator",
              title: (
                <div className="px-[15px] mb-[12px]">
                  <div className="flex items-center space-x-[6px] mb-[10px]">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src="https://assets.smartkarma.com/icons/sparkle.svg"
                      alt="AI Workflows"
                      width={14}
                      height={14}
                    />
                    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                      Agentic Workflows
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-[8px]">
                    {loggedInAccount?.canRequestAIPrimer && (
                      <EntityPrimerButton className="!text-[11px] !font-normal leading-snug !px-[10px] !py-[4px] !rounded-full  !text-[#2a9d78] hover:!text-[#2a9d78] !bg-[#e6f4f0] hover:!bg-[#d0ece5] !border-[1px] !border-[#2a9d78] !border-solid !shadow-none !space-x-0" />
                    )}
                    <EntityComparablesButton className="!text-[11px] !font-normal leading-snug !px-[10px] !py-[4px] !rounded-full !text-[#2a9d78] hover:!text-[#2a9d78] !bg-[#e6f4f0] hover:!bg-[#d0ece5] !border-[1px] !border-[#2a9d78] !border-solid !shadow-none !space-x-0" />
                    {loggedInAccount?.canRequestAIPrimer && (
                      <EntityTopInvestorQuestionButton className="!text-[11px] !font-normal leading-snug !px-[10px] !py-[4px] !rounded-full !text-[#2a9d78] hover:!text-[#2a9d78] !bg-[#e6f4f0] hover:!bg-[#d0ece5] !border-[1px] !border-[#2a9d78] !border-solid !shadow-none !space-x-0" />
                    )}
                    {loggedInAccount?.canRequestAIPrimer && (
                      <SmartScriptButton className="!text-[11px] !font-normal leading-snug !px-[10px] !py-[4px] !rounded-full !text-[#2a9d78] hover:!text-[#2a9d78] !bg-[#e6f4f0] hover:!bg-[#d0ece5] !border-[1px] !border-[#2a9d78] !border-solid !shadow-none !space-x-0" />
                    )}
                  </div>
                </div>
              ),
            },
          ]
        : []),
    ],
    [
      mobile,
      collection,
      loggedInAccount?.isInsightProvider,
      loggedInAccount?.canRequestAIPrimer,
      interfaceSetting?.visitedDashboardWorkspace,
      authenticated,
      setShowFeedSettings,
      toggleModal,
    ]
  );

  const scrollableRef = useRef<HTMLDivElement>(null);
  const { width } = useWindowSize();
  const [showLeftNav, setShowLeftNav] = useState(false);
  const [showRightNav, setShowRightNav] = useState(false);
  const scroll = useCallback(
    (direction) => {
      const scrollable = scrollableRef.current;
      const scrollLeft = scrollable?.scrollLeft || 0;
      const offset = (direction === "right" ? 1 : -1) * Math.round(width / 2);
      scrollable?.scroll({ left: scrollLeft + offset, behavior: "smooth" });
    },
    [width]
  );

  const scrollToTab = useCallback((tab) => {
    const scrollable = scrollableRef.current;

    const scrollLeft =
      tab.offsetLeft -
      (scrollable?.offsetLeft || 0) +
      tab.offsetWidth -
      (scrollable?.scrollWidth || 0) / 2;

    scrollable?.scroll({
      left: scrollLeft,
      behavior: "smooth",
    });
  }, []);

  const onTabClick = useCallback(
    (
      event: React.MouseEvent<HTMLAnchorElement>,
      tab: (typeof tabs)[number]
    ) => {
      try {
        const tab = event.currentTarget;
        scrollToTab(tab);
      } catch (error) {
        console.debug(error);
      }

      try {
        trackEvent("Tab - Click", {
          event_category: "Tab",
          event_action: "Click",
          event_label: tab.name,
        });
      } catch (error) {
        console.debug(error);
      }

      if (tab.href === "/dashboard?collection=workspace") {
        updateInterfaceSetting({
          visitedDashboardWorkspace: true,
        });
      }
    },
    [scrollToTab, trackEvent, updateInterfaceSetting]
  );

  const activeTab = useRef<HTMLSpanElement | null>(null);
  const onTabLoad = useCallback(
    (ref, tab) => {
      if (
        !!ref &&
        (router.asPath === tab.href || router.asPath.startsWith(`${tab.href}?`))
      ) {
        activeTab.current = ref;
      }
    },
    [router.asPath]
  );
  useEffect(() => {
    if (activeTab.current) scrollToTab(activeTab.current);
  }, [scrollToTab]);

  useEffect(() => {
    const scrollable = scrollableRef.current;

    const updateNavButtons = () => {
      if (scrollable?.scrollLeft === 0) {
        setShowLeftNav(false);
      } else {
        setShowLeftNav(true);
      }
      if (
        (scrollable?.scrollLeft || 0) + width >=
        (scrollable?.scrollWidth || 0)
      ) {
        setShowRightNav(false);
      } else {
        setShowRightNav(true);
      }
    };

    updateNavButtons();

    scrollable?.addEventListener("scroll", updateNavButtons, { passive: true });

    return () => {
      scrollable?.removeEventListener("scroll", updateNavButtons);
    };
  }, [width]);

  const [stickyTop, setStickyTop] = useState(false);
  const [top, setTop] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // useEffectOnce(() =>
  //   updateInterfaceSetting({ visitedDashboardWorkspace: false })
  // );

  useEffect(() => {
    if (!mobile) return;

    const upperNavBar = 54;

    const updateTabsPosition = () => {
      const top =
        document.body.getBoundingClientRect()?.top || Number.MAX_SAFE_INTEGER;
      setTimeout(() => {
        setStickyTop(top < -upperNavBar - 42);
        setTop(
          top < -upperNavBar - 42 - 54 ? -upperNavBar : top + upperNavBar + 42
        );
      }, 100);
    };

    updateTabsPosition();

    window.addEventListener("scroll", updateTabsPosition, { passive: true });

    return () => {
      window.removeEventListener("scroll", updateTabsPosition);
    };
  }, [mobile]);

  return (
    <>
      <div
        className={twMerge(
          styles.placeholder,
          mobile && styles.mobileLayout,
          stickyTop && styles.stickyTabs
        )}
        ref={containerRef}
      />
      <div
        className={twMerge(
          styles.dashboardPageTabs,
          mobile && styles.mobileLayout,
          stickyTop && styles.stickyTabs,
          "skiptranslate"
        )}
        style={stickyTop ? { top } : {}}
      >
        {showLeftNav && (
          <button
            title="scroll left"
            className={twMerge(styles.tabsNav, styles.left)}
            onClick={() => scroll("left")}
          >
            <FontAwesomeIcon icon={faAngleDoubleLeft} />
          </button>
        )}
        <div className={styles.tabs} ref={scrollableRef}>
          {tabs.map((tab: any) => {
            if (tab.type === "separator") {
              return (
                <React.Fragment key={tab.key}>
                  <hr className="ml-[10px] mr-[25px] mt-0" />
                  {tab.title}
                </React.Fragment>
              );
            }

            if (tab.type === "custom") {
              return tab.component;
            }

            return (
              <React.Fragment key={tab.name}>
                <Link
                  href={tab.href}
                  rel={tab.noFollow && "nofollow"}
                  prefetch={false}
                  onClick={(e) => onTabClick(e, tab)}
                  className={twMerge(
                    styles.item,
                    router.asPath === tab.href ||
                      router.asPath.startsWith(`${tab.href}&`) ||
                      tab.otherHrefs?.some(
                        (href: string) =>
                          router.asPath === href ||
                          router.asPath.startsWith(`${tab.href}&`)
                      )
                      ? styles.activeTab
                      : undefined
                  )}
                  ref={(ref) => onTabLoad(ref, tab)}
                >
                  {tab.name}
                </Link>
                {tab.additional}
              </React.Fragment>
            );
          })}
        </div>
        {showRightNav && (
          <button
            title="scroll right"
            className={twMerge(styles.tabsNav, styles.right)}
            onClick={() => scroll("right")}
          >
            <FontAwesomeIcon icon={faAngleDoubleRight} />
          </button>
        )}
      </div>
    </>
  );
};

export default DashboardPageTabs;
